// Singleton SSE client for the extension server's EventBus.
//
// Connects to:
//   GET /api/integrations/events/stream — `event` SSE channel, payload `StreamEvent`
//   GET /api/integrations/chat/stream   — `chat`  SSE channel, payload `ChatMessage`
//
// Maintains an in-memory ring buffer for chat (200 msgs) so consumers
// that mount mid-stream can replay recent context. The bridge layer
// (`eventBusBridge.ts`) is responsible for fanning events out to atoms,
// the TriggerEngine and the Dexie stats repository — this service just
// handles the wire protocol.
//
// `start()` is idempotent: calling it twice is a no-op (each SSE handle
// is only created when its slot is null). The reconnection strategy is
// a flat 5s retry on `onerror`; backoff is intentionally trivial because
// the server lives on localhost and disconnects are local-only.

import type { StreamEvent, ChatMessage } from "./types";
import { localApiPort } from "../../lib/localApi";

type EventHandler = (e: StreamEvent) => void;
type ChatHandler = (c: ChatMessage) => void;
type ConnectionHandler = (active: boolean) => void;

const CHAT_BUFFER_LIMIT = 200;
const RECONNECT_DELAY_MS = 5_000;

class EventBusService {
  private eventSse: EventSource | null = null;
  private chatSse: EventSource | null = null;
  private eventHandlers = new Set<EventHandler>();
  private chatHandlers = new Set<ChatHandler>();
  private connectionHandlers = new Set<ConnectionHandler>();
  private chatConnectionHandlers = new Set<ConnectionHandler>();
  private chatActive = false;
  private chatBuffer: ChatMessage[] = [];
  private chatSeen = new Set<string>();
  private active = false;
  private reconnectTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * Open both SSE streams. Idempotent — if a stream is already open it
   * is left alone, so calling `start()` from multiple mount points is
   * safe and won't duplicate connections.
   */
  start(): void {
    if (!this.eventSse) this.connectEvents();
    if (!this.chatSse) this.connectChat();
  }

  /** Close both streams and cancel any pending reconnect timer. */
  stop(): void {
    this.eventSse?.close();
    this.chatSse?.close();
    this.eventSse = null;
    this.chatSse = null;
    this.reconnectTimers.forEach(clearTimeout);
    this.reconnectTimers = [];
    this.setActive(false);
    this.setChatActive(false);
  }

  private port(): string {
    return String(localApiPort());
  }

  private connectEvents(): void {
    const url = `http://localhost:${this.port()}/api/integrations/events/stream`;
    const sse = new EventSource(url);
    this.eventSse = sse;
    sse.addEventListener("event", (msg: MessageEvent) => {
      try {
        const e = JSON.parse(msg.data) as StreamEvent;
        this.eventHandlers.forEach((h) => {
          try {
            h(e);
          } catch (err) {
            console.error("[eventBus] event handler failed", err);
          }
        });
      } catch (err) {
        console.error("[eventBus] event parse error", err);
      }
    });
    sse.onopen = () => this.setActive(true);
    sse.onerror = () => {
      // Browser EventSource auto-reconnects on its own when the server
      // is reachable again, but our SSE endpoint may be down entirely
      // (extension not running). Close + retry manually so we never
      // leak a permanently-failed handle.
      sse.close();
      if (this.eventSse === sse) this.eventSse = null;
      this.setActive(false);
      const t = setTimeout(() => {
        if (!this.eventSse) this.connectEvents();
      }, RECONNECT_DELAY_MS);
      this.reconnectTimers.push(t);
    };
  }

  private connectChat(): void {
    const url = `http://localhost:${this.port()}/api/integrations/chat/stream`;
    const sse = new EventSource(url);
    this.chatSse = sse;
    sse.onopen = () => this.setChatActive(true);
    sse.addEventListener("chat", (msg: MessageEvent) => {
      try {
        const c = JSON.parse(msg.data) as ChatMessage;
        // El stream reenvía su buffer entero en CADA conexión, así que tras
        // una reconexión vuelven mensajes que ya tenemos: sin este corte se
        // duplican en pantalla y React repite keys.
        const key = `${c.platform}:${c.id}`;
        if (this.chatSeen.has(key)) return;
        this.chatSeen.add(key);
        this.chatBuffer.push(c);
        if (this.chatBuffer.length > CHAT_BUFFER_LIMIT) {
          const dropped = this.chatBuffer.shift();
          if (dropped) this.chatSeen.delete(`${dropped.platform}:${dropped.id}`);
        }
        this.chatHandlers.forEach((h) => {
          try {
            h(c);
          } catch (err) {
            console.error("[eventBus] chat handler failed", err);
          }
        });
      } catch (err) {
        console.error("[eventBus] chat parse error", err);
      }
    });
    sse.onerror = () => {
      sse.close();
      if (this.chatSse === sse) this.chatSse = null;
      this.setChatActive(false);
      const t = setTimeout(() => {
        if (!this.chatSse) this.connectChat();
      }, RECONNECT_DELAY_MS);
      this.reconnectTimers.push(t);
    };
  }

  private setActive(v: boolean): void {
    if (this.active === v) return;
    this.active = v;
    this.connectionHandlers.forEach((h) => {
      try {
        h(v);
      } catch (err) {
        console.error("[eventBus] connection handler failed", err);
      }
    });
  }

  onEvent(h: EventHandler): () => void {
    this.eventHandlers.add(h);
    return () => {
      this.eventHandlers.delete(h);
    };
  }

  private setChatActive(value: boolean): void {
    if (this.chatActive === value) return;
    this.chatActive = value;
    // Igual que setActive: un handler que explota no puede cortar el aviso a
    // los demás ni el reintento de conexión del chat.
    this.chatConnectionHandlers.forEach((handler) => {
      try {
        handler(value);
      } catch (err) {
        console.error("[eventBus] chat connection handler failed", err);
      }
    });
  }

  onChatConnection = (handler: ConnectionHandler): (() => void) => {
    this.chatConnectionHandlers.add(handler);
    return () => { this.chatConnectionHandlers.delete(handler); };
  };

  isChatActive = (): boolean => this.chatActive;

  onChat(h: ChatHandler): () => void {
    this.chatHandlers.add(h);
    return () => {
      this.chatHandlers.delete(h);
    };
  }

  onConnection(h: ConnectionHandler): () => void {
    this.connectionHandlers.add(h);
    return () => {
      this.connectionHandlers.delete(h);
    };
  }

  /** Snapshot copy of the chat ring buffer. */
  getChatBuffer(): ChatMessage[] {
    return [...this.chatBuffer];
  }

  isActive(): boolean {
    return this.active;
  }
}

export const eventBusService = new EventBusService();
export type { EventBusService };
